import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildCloseButton, buildForm, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';
import type { WheelSegment } from './templates';

/** Gamified spin-to-win: the lead-capture form first ("Spin to win"), then a weighted-random
 * CSS-animated spin over `theme.wheel.segments`, revealing that segment's label and (optionally)
 * an existing discount code. Weighting uses the same weighted-random approach
 * variantBucketing.ts already implements for A/B variants — just not persisted, since a spin only
 * happens once per interaction rather than needing to stay sticky across page loads. */
const STYLE = `
  ${SHARED_PANEL_STYLE}
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; z-index: 2147483000;
  }
  .panel { width: min(420px, 92vw); text-align: center; }
  .wheel-wrap { position: relative; width: 220px; height: 220px; margin: 0 auto 20px; }
  .wheel {
    width: 100%; height: 100%; border-radius: 50%; position: relative;
    transition: transform 4.5s cubic-bezier(0.12, 0.72, 0.18, 1);
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  }
  .wheel-pointer {
    position: absolute; top: -8px; left: 50%; transform: translateX(-50%);
    width: 0; height: 0; border-left: 10px solid transparent; border-right: 10px solid transparent;
    border-top: 16px solid currentColor; z-index: 1;
  }
  .segment-label {
    position: absolute; left: 50%; top: 10px; transform-origin: 0 100px;
    font-size: 11px; font-weight: 700; text-shadow: 0 1px 2px rgba(0,0,0,0.25);
    width: 90px; text-align: center; margin-left: -45px;
  }
  form.spin-form { display: block; }
  form.spin-form.hidden { display: none; }
  .prize { display: none; }
  .prize.revealed { display: block; }
  .prize-code {
    display: inline-block; margin: 4px 0 16px; padding: 8px 16px; border: 1px dashed currentColor;
    border-radius: 6px; font-weight: 700; letter-spacing: 0.05em; cursor: pointer; background: none;
    color: inherit; font-size: 14px;
  }
`;

/** White text over a light wedge color (e.g. a pastel) reads as almost invisible even with a
 * shadow — pick black or white per segment from its own relative luminance instead of assuming
 * every wheel is dark-wedges-only. Standard WCAG-ish luminance formula, not exact color science,
 * just enough to reliably pick the more readable of the two. */
function readableTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  if (hex.length !== 3 && hex.length !== 6) return '#fff';
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  if ([r, g, b].some(Number.isNaN)) return '#fff';
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.6 ? '#111827' : '#fff';
}

function weightedPick(segments: WheelSegment[]): WheelSegment {
  const totalWeight = segments.reduce((sum, s) => sum + Math.max(s.weight, 0), 0);
  if (totalWeight <= 0) return segments[0];
  let roll = Math.random() * totalWeight;
  for (const segment of segments) {
    roll -= Math.max(segment.weight, 0);
    if (roll <= 0) return segment;
  }
  return segments[segments.length - 1];
}

/** Sweep angles proportional to weight, evenly split when every weight is 0/unset. */
function sweepAngles(segments: WheelSegment[]): { segment: WheelSegment; start: number; end: number; mid: number }[] {
  const totalWeight = segments.reduce((sum, s) => sum + Math.max(s.weight, 0), 0);
  const equalShare = 360 / segments.length;
  let angle = 0;
  return segments.map((segment) => {
    const sweep = totalWeight > 0 ? (Math.max(segment.weight, 0) / totalWeight) * 360 : equalShare;
    const start = angle;
    const end = angle + sweep;
    angle = end;
    return { segment, start, end, mid: (start + end) / 2 };
  });
}

// The wheel visual replaces the usual image/body/countdown/recommendations header entirely, so
// this doesn't call buildHeader (and therefore doesn't need `client`) — kept in the signature only
// so render/index.ts's dispatch table can call every renderer uniformly.
export function renderCouponWheel(
  form: LeadFormPublicConfig,
  _client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const segments = (theme.wheel?.segments ?? []).filter((s) => s.label);
  const { host, root } = createHost(STYLE);

  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const panel = document.createElement('div');
  panel.className = 'panel';
  applyThemeVars(panel, resolved);
  panel.appendChild(buildCloseButton(close));

  if (theme.headline) {
    const headline = document.createElement('p');
    headline.className = 'headline';
    headline.textContent = theme.headline;
    panel.appendChild(headline);
  }

  const slices = sweepAngles(segments);

  const wheelWrap = document.createElement('div');
  wheelWrap.className = 'wheel-wrap';
  const pointer = document.createElement('div');
  pointer.className = 'wheel-pointer';
  const wheel = document.createElement('div');
  wheel.className = 'wheel';
  wheel.style.background = segments.length > 0
    ? `conic-gradient(${slices.map((s) => `${s.segment.color} ${s.start}deg ${s.end}deg`).join(', ')})`
    : '#ddd';
  for (const slice of slices) {
    const label = document.createElement('span');
    label.className = 'segment-label';
    label.textContent = slice.segment.label;
    label.style.transform = `rotate(${slice.mid}deg)`;
    label.style.color = readableTextColor(slice.segment.color);
    wheel.appendChild(label);
  }
  wheelWrap.appendChild(pointer);
  wheelWrap.appendChild(wheel);
  panel.appendChild(wheelWrap);

  const formEl = buildForm(form.fields, theme.ctaLabel || 'Spin to win', onSubmit, spin);
  formEl.classList.add('spin-form');
  panel.appendChild(formEl);

  const prize = document.createElement('div');
  prize.className = 'prize';
  panel.appendChild(prize);

  overlay.appendChild(panel);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  root.appendChild(overlay);

  function spin() {
    formEl.classList.add('hidden');
    if (slices.length === 0) {
      revealPrize(null);
      return;
    }
    const winner = weightedPick(segments);
    const winningSlice = slices.find((s) => s.segment.id === winner.id) ?? slices[0];
    const fullSpins = 5 * 360;
    const rotation = fullSpins + ((360 - winningSlice.mid) % 360);
    wheel.style.transform = `rotate(${rotation}deg)`;

    wheel.addEventListener('transitionend', () => revealPrize(winner), { once: true });
  }

  function revealPrize(winner: WheelSegment | null) {
    prize.replaceChildren();
    const message = document.createElement('p');
    message.className = 'headline';
    message.textContent = winner ? `You won: ${winner.label}!` : 'Thanks for playing!';
    prize.appendChild(message);

    if (winner?.discountCode) {
      const codeButton = document.createElement('button');
      codeButton.type = 'button';
      codeButton.className = 'prize-code';
      codeButton.textContent = winner.discountCode;
      codeButton.title = 'Copy code';
      codeButton.addEventListener('click', () => {
        navigator.clipboard?.writeText(winner.discountCode!).then(() => {
          codeButton.textContent = 'Copied!';
          window.setTimeout(() => {
            codeButton.textContent = winner.discountCode!;
          }, 1500);
        });
      });
      prize.appendChild(codeButton);
    }

    prize.classList.add('revealed');
  }

  function close() {
    host.remove();
  }

  return { close };
}
